import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { db } from "@/modules/shared/lib/db-client";
import openhubLogoFallback from "@/assets/openhub-logo.png";
import openhubLogoWhiteFallback from "@/assets/openhub-logo-white.png";

interface BrandingData {
  logoUrl: string;
  logoWhiteUrl: string;
  platformName: string;
  primaryColor: string;
  accentColor: string;
  faviconUrl: string | null;
  isLoading: boolean;
}

function hexToHsl(hex: string): string | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return null;

  let r = parseInt(result[1], 16) / 255;
  let g = parseInt(result[2], 16) / 255;
  let b = parseInt(result[3], 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

export function useBranding(): BrandingData {
  const { data, isLoading } = useQuery({
    queryKey: ["branding"],
    queryFn: async () => {
      const { data: raw, error } = await db
        .from("settings")
        .select("config")
        .eq("scope", "company")
        .eq("scope_key", "_default")
        .maybeSingle();

      if (error) throw error;
      if (!raw?.config) return null;

      const c = raw.config as Record<string, any>;
      return {
        logo_url: c.logo_url ?? null,
        logo_white_url: c.logo_white_url ?? null,
        favicon_url: c.favicon_url ?? null,
        brand_primary_color: c.brand_primary_color ?? null,
        brand_accent_color: c.brand_accent_color ?? null,
        platform_name: c.platform_name ?? null,
      };
    },
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
  });

  const primaryColor = data?.brand_primary_color || "#4964A2";
  const accentColor = data?.brand_accent_color || "#CED23C";
  const faviconUrl = data?.favicon_url || null;

  // Dynamic favicon
  useEffect(() => {
    if (!faviconUrl) return;
    let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.href = faviconUrl;
  }, [faviconUrl]);

  // Dynamic CSS custom properties — applies white-label colors globally
  useEffect(() => {
    const root = document.documentElement;

    const primaryHsl = hexToHsl(primaryColor);
    const accentHsl = hexToHsl(accentColor);

    if (primaryHsl) {
      root.style.setProperty("--primary", primaryHsl);
      root.style.setProperty("--ring", primaryHsl);
      root.style.setProperty("--brand-blue", primaryHsl);
    }

    if (accentHsl) {
      root.style.setProperty("--accent", accentHsl);
      root.style.setProperty("--brand-lime", accentHsl);
      root.style.setProperty("--sidebar-primary", accentHsl);
      root.style.setProperty("--sidebar-ring", accentHsl);
    }

    return () => {
      root.style.removeProperty("--primary");
      root.style.removeProperty("--ring");
      root.style.removeProperty("--brand-blue");
      root.style.removeProperty("--accent");
      root.style.removeProperty("--brand-lime");
      root.style.removeProperty("--sidebar-primary");
      root.style.removeProperty("--sidebar-ring");
    };
  }, [primaryColor, accentColor]);

  return {
    logoUrl: data?.logo_url || openhubLogoFallback,
    logoWhiteUrl: data?.logo_white_url || openhubLogoWhiteFallback,
    platformName: data?.platform_name || "OpenHub",
    primaryColor,
    accentColor,
    faviconUrl,
    isLoading,
  };
}
